// Die Leiste ist immer da, im Ruhezustand aber durchsichtig: nur Logo und Hamburger.
// Mit der Maus geht sie auf, sobald der Zeiger im oberen Teil des Fensters ist, sonst über den Hamburger.
export function initSiteNav() {
  const header = document.getElementById("site-nav");
  if (!header) return;

  const toggle = header.querySelector(".nav-toggle");
  const links = header.querySelector(".nav-links");
  const arrow = document.getElementById("arrow");

  // Konfiguration
  const POINTER_ZONE = 0.4; // Anteil der Fensterhöhe, von oben gemessen
  const CLOSE_DELAY = 150; // kurz warten, damit die Leiste an der Grenze nicht flackert
  const SCROLL_THRESHOLD = 100;

  // Links untereinander, X statt Strich; gleiche Grenze wie in _nav.scss
  const stacked = window.matchMedia("(max-width: 600px)");

  // State
  let pointerInZone = false;
  let pointerOpen = false;
  let menuOpen = false; // über den Hamburger geöffnet (Touch, Tastatur)
  let dismissed = false; // per Klick zugemacht, obwohl die Maus noch oben ist
  let closeTimer;
  let rafPending = false;

  const isOpen = () => menuOpen || (pointerOpen && !dismissed);

  const render = () => {
    const box = toggle.getBoundingClientRect();
    const onLight = isLightSurface(header, box.left + box.width / 2, box.top + box.height / 2);
    const open = isOpen();

    header.classList.toggle("is-open", open);
    header.classList.toggle("on-light", onLight);
    toggle.setAttribute("aria-expanded", String(open));
    arrow?.classList.toggle("hide", window.scrollY > SCROLL_THRESHOLD);

    rafPending = false;
  };

  const requestUpdate = () => {
    if (!rafPending) {
      requestAnimationFrame(render);
      rafPending = true;
    }
  };

  const close = ({ dismiss = false } = {}) => {
    menuOpen = false;
    if (dismiss) dismissed = pointerOpen;
    requestUpdate();
  };

  const setPointerInZone = (inZone) => {
    if (inZone === pointerInZone) return;
    pointerInZone = inZone;
    clearTimeout(closeTimer);

    if (inZone) {
      pointerOpen = true;
      requestUpdate();
    } else {
      closeTimer = setTimeout(() => {
        pointerOpen = false;
        dismissed = false;
        requestUpdate();
      }, CLOSE_DELAY);
    }
  };

  // Nur eine echte Maus öffnet über die Position, Touch und die Handy-Ansicht gehen über den Hamburger
  window.addEventListener(
    "pointermove",
    (event) => {
      if (event.pointerType !== "mouse" || stacked.matches) return;
      setPointerInZone(event.clientY < window.innerHeight * POINTER_ZONE);
    },
    { passive: true }
  );

  document.documentElement.addEventListener("mouseleave", () => setPointerInZone(false));

  toggle.addEventListener("click", () => {
    if (isOpen()) {
      close({ dismiss: true });
    } else {
      menuOpen = true;
      requestUpdate();
    }
  });

  // Wer per Tastatur in den Links steht, soll sie nicht verlieren, nur weil die Maus wegrutscht
  links.addEventListener("focusin", (event) => {
    if (event.target.matches(":focus-visible")) menuOpen = true;
  });

  // Per Tastatur aus der Leiste hinaus. Bei Maus und Touch übernimmt das der Klick daneben, sonst wäre
  // das Menü schon zu, bevor der Klick ankommt, und der Tipp würde doch noch etwas darunter auslösen.
  document.addEventListener("focusin", (event) => {
    if (menuOpen && !header.contains(event.target) && event.target.matches(":focus-visible")) close();
  });

  // Auf dem Handy schliesst ein Tipp daneben nur das Menü, statt auch gleich etwas darunter anzuklicken
  document.addEventListener(
    "click",
    (event) => {
      if (!menuOpen || header.contains(event.target)) return;
      if (stacked.matches) {
        event.preventDefault();
        event.stopPropagation();
      }
      close();
    },
    { capture: true }
  );

  stacked.addEventListener("change", () => {
    setPointerInZone(false);
    close();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !isOpen()) return;
    if (links.contains(document.activeElement)) toggle.focus();
    close({ dismiss: true });
  });

  window.addEventListener("scroll", requestUpdate, { passive: true });
  window.addEventListener("resize", requestUpdate);

  markActiveLink(header);
  render();
}

// Liegt unter dem Hamburger gerade eine helle Fläche? Gezählt wird nur, was mindestens halb so breit
// ist wie das Fenster (Abschnitte, Bänder), damit Karten und Bilder die Farbe nicht hin und her springen lassen.
function isLightSurface(header, x, y) {
  for (const element of document.elementsFromPoint(x, y)) {
    if (header.contains(element) || !(element.offsetWidth >= window.innerWidth / 2)) continue;

    const style = getComputedStyle(element);
    const [r, g, b, alpha = 1] = style.backgroundColor.match(/[\d.]+/g)?.map(Number) ?? [0, 0, 0, 0];
    if (alpha >= 0.5) return 0.2126 * r + 0.7152 * g + 0.0722 * b > 140;
    if (style.backgroundImage.includes("url(")) return false; // unsere Fotos sind dunkel abgetönt
  }

  return false;
}

function markActiveLink(header) {
  const path = window.location.pathname;

  header.querySelectorAll("[data-route]").forEach((link) => {
    const route = link.dataset.route;
    const isActive = path === route || path.startsWith(`${route}/`);

    link.classList.toggle("active", isActive);
    if (isActive) {
      link.setAttribute("aria-current", "page");
    } else {
      link.removeAttribute("aria-current");
    }
  });
}
